import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Trophy, Loader2, MapPin, Medal } from "lucide-react";

export default function TournamentHistoryModal({ playerId, playerName, onClose }) {
  const [hist, setHist] = useState(null);
  const [records, setRecords] = useState([]);
  useEffect(() => {
    api.get(`/players/${playerId}/tournament-history`).then(({ data }) => {
      setHist(Array.isArray(data) ? data : data.tournaments || []);
      setRecords(Array.isArray(data) ? [] : data.records || []);
    }).catch(() => setHist([]));
  }, [playerId]);
  return (
    <div className="fixed inset-0 z-[85] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="tournament-history-modal" className="w-full max-w-lg bg-pb-surface border border-yellow-500/40 p-5 my-8 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <span className="flex items-center gap-2 font-display font-black uppercase text-yellow-400">
            <Trophy className="w-4 h-4" /> Tournament History
          </span>
          <button data-testid="tournament-history-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-zinc-500 mb-4">{playerName}</div>
        {!hist ? (
          <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 text-yellow-400 animate-spin" /></div>
        ) : hist.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center" data-testid="tournament-history-empty">No tournaments played yet — their story starts with the next event.</div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const golds = hist.filter((t) => t.champion).length;
              const silvers = hist.filter((t) => t.runner_up).length;
              const bronzes = hist.filter((t) => t.third).length;
              const wins = hist.reduce((n, t) => n + t.matches.filter((m) => m.won).length, 0);
              const losses = hist.reduce((n, t) => n + t.matches.filter((m) => !m.won).length, 0);
              return (
                <div className="grid grid-cols-5 gap-2 text-center" data-testid="history-medal-count">
                  {[[golds, "Gold", "text-yellow-400", Trophy], [silvers, "Silver", "text-zinc-300", Medal],
                    [bronzes, "Bronze", "text-amber-600", Medal],
                    [wins, "Wins", "text-green-400", null], [losses, "Losses", "text-red-400", null]].map(([v, l, c, Icon]) => (
                    <div key={l} className="border border-pb-border bg-pb-elevated/40 p-2">
                      <div className={`font-display font-black text-lg ${c} flex items-center justify-center gap-1`}>
                        {Icon && <Icon className={`w-3.5 h-3.5 ${l === "Gold" ? "fill-yellow-400" : l === "Bronze" ? "fill-amber-700" : ""}`} />}{v}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {records.length > 0 && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="history-discipline-records">
                {records.map((r) => (
                  <div key={r.label} className="border border-pb-cyan/25 bg-pb-cyan/5 p-2" data-testid={`history-record-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="font-display font-black text-lg">
                      <span className="text-green-400">{r.wins}</span>
                      <span className="text-zinc-600 mx-0.5">–</span>
                      <span className="text-red-400">{r.losses}</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-pb-cyan">{r.label}</div>
                    <div className="text-[8px] uppercase tracking-widest text-zinc-600">W – L</div>
                  </div>
                ))}
              </div>
            )}
            {hist.map((t) => (
              <div key={t.tournament_id} className="border border-pb-border bg-pb-elevated/40 p-3.5" data-testid={`history-tournament-${t.tournament_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-black uppercase text-base text-white leading-tight flex items-center gap-2">
                      {t.name}
                      {t.champion && <Trophy className="w-4 h-4 text-yellow-400 fill-yellow-400 shrink-0" title="Champion" />}
                      {!t.champion && t.runner_up && <Medal className="w-4 h-4 text-zinc-300 shrink-0" title="Runner-up" />}
                      {!t.champion && !t.runner_up && t.third && <Medal className="w-4 h-4 text-amber-600 fill-amber-700 shrink-0" title="Bronze medalist" />}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-zinc-500 mt-0.5">
                      <span>{t.date}</span>
                      {t.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.location}</span>}
                      {t.events?.length > 0 && <span className="text-pb-cyan">{t.events.join(" · ")}</span>}
                    </div>
                  </div>
                  {t.status !== "complete" && <span className="text-[9px] uppercase tracking-widest text-green-400 border border-green-500/40 px-1.5 py-0.5 shrink-0">{t.status === "in_progress" ? "Live" : "Upcoming"}</span>}
                </div>
                {t.matches.length > 0 ? (
                  <div className="mt-2.5 space-y-1">
                    {t.matches.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`history-match-${t.tournament_id}-${i}`}>
                        <span className={`w-4 shrink-0 font-black ${m.won ? "text-green-400" : "text-red-400"}`}>{m.won ? "W" : "L"}</span>
                        <span className="text-zinc-400 truncate flex-1">vs {m.opponent}</span>
                        <span className="font-mono font-bold text-white shrink-0">{m.score}</span>
                        {m.games && <span className="text-[10px] text-zinc-600 font-mono shrink-0">({m.games})</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-zinc-600">No scores recorded{t.status !== "complete" ? " yet" : ""} · entered as {t.team}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
